/**
 * The theme control — one icon button cycling `system → light → dark`.
 *
 * ⭐ PORTED FROM `cloud/dp/shell/src/platform/PolarityToggle.tsx` because the owner asked for *that*
 * widget: same three-stop cycle, same `Monitor`/`Sun`/`Moon` mapping, same 32px square icon button,
 * same position (first in the header's right-hand cluster, `Header.tsx:291-293`), and — like the
 * original — rendered OUTSIDE the signed-in guard, so a principal with no visible section can still
 * choose a theme. ⛔ Read, never imported: `cloud/dp/shell` is a different package.
 *
 * ## Why a 3-stop CYCLE at all, given Hick's law and recognition-over-recall
 *
 * A cycle hides its options — a menu would show all three at once. It is still the right control
 * here: `system` is not a peer of the other two but the DEFAULT, the stops are ordered
 * least-to-most committed, and one square button in permanent chrome costs nothing next to a menu
 * that must be opened to be read. The state is recoverable in one press either way, and the
 * accessible name (below) names the next stop, so nothing is hidden from a screen-reader user
 * either. ⚠️ If a fourth stop is ever added this stops being true and it must become a menu.
 *
 * ## Two divergences from the DP original, both fixes
 *
 * ⛔ 1. THE ACCESSIBLE NAME STATES THE CONSEQUENCE. DP's is `aria-label={LABELS[polarity]}` —
 *       `"System theme"` — a button NAMED AFTER ITS OWN CURRENT VALUE. A screen-reader user hears
 *       "System theme, button" and is told nothing about what pressing it does; on a three-way
 *       cycle that is unusable. This one names both: where you are, then where the press takes you.
 *       ⛔ NOT via a live region. `SectionLayout`'s grants banner is already this screen's one
 *       `role="status"`, and a second live region in permanent chrome re-announces on render and
 *       buries the transient message that actually matters.
 * ⛔ 2. A VISIBLE FOCUS RING, this package's `focus-visible:outline-*` house pattern. The original
 *       styles `hover:` only, which makes an icon-only control effectively mouse-only.
 *
 * `title` deliberately carries the same string as the label — for a sighted mouse user the icon
 * alone does not say which of the three stops is active (`Monitor` in particular reads as "display
 * settings"), and the tooltip is where they will look.
 */
import { Monitor, Moon, Sun } from 'lucide-react';
import type { ShellPolarity } from '@zion/sdk-contracts';
import { usePolarityContext } from './PolarityContext';

/**
 * ⛔ ORDER IS THE DESIGN: least-committed first. `system` (defer to the OS) → `light` → `dark`, and
 * `dark` wraps back to `system` so the default is always one press away rather than two.
 */
const CYCLE: readonly ShellPolarity[] = ['system', 'light', 'dark'];

const ICONS = { system: Monitor, light: Sun, dark: Moon } as const;

export function nextPolarity(current: ShellPolarity): ShellPolarity {
  // `indexOf` cannot miss — `current` is the closed `ShellPolarity` union and CYCLE holds all three
  // members — so there is no not-found branch to cover, by construction rather than by assumption.
  return CYCLE[(CYCLE.indexOf(current) + 1) % CYCLE.length];
}

/** "Theme: system. Switch to light." — state, then consequence, in one accessible name. */
export function polarityLabel(current: ShellPolarity): string {
  return `Theme: ${current}. Switch to ${nextPolarity(current)}.`;
}

export function PolarityToggle(): React.JSX.Element {
  const { polarity, setPolarity } = usePolarityContext();
  const Icon = ICONS[polarity];
  const label = polarityLabel(polarity);
  return (
    <button
      type="button"
      onClick={() => setPolarity(nextPolarity(polarity))}
      data-testid="polarity-toggle"
      aria-label={label}
      title={label}
      className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-muted hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring"
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}
